import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Check } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { LandingReveal } from './landing-shared';

const FREE_PLANS = ['community', 'starter'] as const;
const FREE_PLAN_FEATURE_KEYS = ['0', '1', '2', '3', '4'] as const;

const FreePlanCard = ({
  plan,
  signedIn,
}: {
  plan: (typeof FREE_PLANS)[number];
  signedIn: boolean;
}) => {
  const { t } = useTranslation('landing');

  return (
    <article className="landing-free-plan-card flex h-full flex-col rounded-2xl p-6 sm:p-8">
      <p className="text-body-sm font-semibold uppercase tracking-[0.16em] text-on-brand/75">
        {t(`freePlans.plans.${plan}.label`)}
      </p>
      <h2 className="mt-3 text-card-title font-semibold text-on-brand">{t(`pricing.plans.${plan}.name`)}</h2>
      <p className="landing-free-plan-eligibility mt-4 inline-block self-start bg-on-brand text-brand">
        {t(`freePlans.plans.${plan}.eligibility`)}
      </p>
      <p className="mt-5 text-pretty text-body leading-relaxed text-on-brand/90">
        {t(`freePlans.plans.${plan}.body`)}
      </p>

      <ul className="mt-6 grid flex-1 gap-3">
        {FREE_PLAN_FEATURE_KEYS.map((featureKey) => (
          <li key={featureKey} className="flex gap-3 text-body-sm leading-snug text-on-brand/90">
            <span className="landing-free-plan-check mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full">
              <Check className="size-3 stroke-[2.5]" aria-hidden />
            </span>
            <span>{t(`pricing.plans.${plan}.features.${featureKey}`)}</span>
          </li>
        ))}
      </ul>

      {!signedIn ? (
        <div className="mt-8">
          <Button
            size="lg"
            className="w-full bg-on-brand text-brand shadow-e2 hover:bg-on-brand/90 active:bg-on-brand/85"
            asChild
          >
            <Link to="/register">{t(`pricing.plans.${plan}.cta`)}</Link>
          </Button>
        </div>
      ) : null}
    </article>
  );
};

export const LandingFreePlans = ({ signedIn }: { signedIn: boolean }) => {
  const { t } = useTranslation('landing');

  return (
    <LandingReveal>
      <p className="mx-auto max-w-2xl text-pretty text-center text-body leading-relaxed text-on-brand/90">
        {t('freePlans.intro')}
      </p>

      <ul className="mt-10 grid gap-6 lg:grid-cols-2 lg:gap-8">
        {FREE_PLANS.map((plan) => (
          <li key={plan}>
            <FreePlanCard plan={plan} signedIn={signedIn} />
          </li>
        ))}
      </ul>

      <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        {signedIn ? (
          <Button
            size="lg"
            className="w-full min-w-[11.75rem] bg-on-brand text-brand shadow-e2 hover:bg-on-brand/90 active:bg-on-brand/85 sm:w-auto"
            asChild
          >
            <Link to="/home">{t('nav.dashboard')}</Link>
          </Button>
        ) : (
          <Button
            size="lg"
            className="w-full min-w-[11.75rem] bg-on-brand text-brand shadow-e2 hover:bg-on-brand/90 active:bg-on-brand/85 sm:w-auto"
            asChild
          >
            <Link to="/register">{t('freePlans.cta')}</Link>
          </Button>
        )}
        <Button
          size="lg"
          variant="outline"
          className="landing-free-plans-cta-secondary w-full min-w-[11.75rem] sm:w-auto"
          asChild
        >
          <Link to="/pricing">{t('freePlans.pricingLink')}</Link>
        </Button>
      </div>

      <p className="mx-auto mt-8 max-w-2xl text-center text-body-sm text-on-brand/85">{t('freePlans.note')}</p>
    </LandingReveal>
  );
};
